import { useState, type FormEvent } from "react";
import { DiscordPreview, SitePreview } from "./AnnouncementPreview.tsx";
import { api, useAction, useApi } from "../../api.ts";
import { fmtDateTime, fmtRelative } from "../../format.ts";
import {
  Badge, Button, Card, EmptyState, ErrorText, Field, IconButton, Input, Loading, LoadError, Mono, PageHeader, Select, Switch, Table,
  TBody, Td, Textarea, Th, THead, Tabs, Tr, Dialog, useConfirm, useToast,
} from "../../ui/index.tsx";
import { Prose } from "../_shared.tsx";
import { Markdown, MarkdownEditor } from "../../ui/markdown.tsx";
import { FormActions } from "./_ops.tsx";
import type { Announcement, AnnouncementLevel, Guide, GuideSummary } from "../../../shared/types.ts";

type Tab = "announcements" | "guides";

export default function Content() {
  const [tab, setTab] = useState<Tab>("announcements");
  return (
    <>
      <PageHeader title="Content" description="Announcements pushed to users and the guides shown on the site." />
      <Tabs<Tab> value={tab} onChange={setTab} tabs={[{ id: "announcements", label: "Announcements" }, { id: "guides", label: "Guides" }]} />
      <div className="mt-6">{tab === "announcements" ? <Announcements /> : <Guides />}</div>
    </>
  );
}

const levelTone = { info: "info", warning: "warn", critical: "bad" } as const;

function Announcements() {
  const list = useApi<Announcement[]>("/admin/announcements");
  const toast = useToast();
  const confirm = useConfirm();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState<AnnouncementLevel>("info");
  const [discord, setDiscord] = useState(true);
  const [everyone, setEveryone] = useState(false);
  const [withPoll, setWithPoll] = useState(false);
  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState(["", ""]);
  const [hours, setHours] = useState("168");
  const [banner, setBanner] = useState(false);
  const BANNER = "https://exoware.app/media/logo-lockup-dark.png";

  const publish = useAction(async () => {
    if (everyone && !(await confirm({ title: "Ping @everyone?", body: "Every member of the Discord server gets a notification. This can't be undone.", confirmLabel: "Ping everyone" }))) return;
    await api.post("/admin/announcements", {
      title: title.trim(), message: message.trim(), level, discord,
      ping_everyone: discord && everyone,
      image_url: discord && banner ? BANNER : undefined,
      poll: discord && withPoll ? { question: question.trim(), answers: answers.map((a) => a.trim()).filter(Boolean), hours: Number(hours) || 72 } : undefined,
    });
    setTitle(""); setMessage(""); setLevel("info"); setEveryone(false); setBanner(false); setWithPoll(false); setQuestion(""); setAnswers(["", ""]);
    toast(discord ? "Published to the site and Discord" : "Announcement published", "success");
    await list.reload();
  });

  const toggle = async (a: Announcement, active: boolean) => {
    try {
      await api.patch(`/admin/announcements/${a.id}`, { active });
      list.setData((prev) => (prev ?? []).map((x) => (x.id === a.id ? { ...x, active } : x)));
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  const remove = async (a: Announcement) => {
    if (!await confirm({ title: "Delete announcement?", body: `"${a.title}" will be removed permanently.`, confirmLabel: "Delete", danger: true })) return;
    try {
      await api.del(`/admin/announcements/${a.id}`);
      list.setData((prev) => (prev ?? []).filter((x) => x.id !== a.id));
      toast("Announcement deleted", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Delete failed", "error");
    }
  };

  const submit = (e: FormEvent) => { e.preventDefault(); void publish.run(); };

  return (
    <div className="space-y-6">
    <div className="grid gap-6 xl:grid-cols-[26rem_1fr]">
      <Card title="New announcement" description="Shown on the site to every connected user, and optionally posted to Discord in the house style.">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required /></Field>
          <Field label="Message" hint={`${message.length}/2000 · Select text and use the toolbar, or type Discord markdown. Enter continues a list.`}>
            <MarkdownEditor value={message} onChange={setMessage} maxLength={2000} rows={9} placeholder={"What's new?\n\n- point one\n- point two"} />
          </Field>
          <Field label="Level">
            <Select value={level} onChange={(e) => setLevel(e.target.value as AnnouncementLevel)}>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </Select>
          </Field>
          <div className="flex flex-col items-start gap-3 rounded-lg border border-line bg-bg/50 p-3">
            <Switch checked={discord} onChange={setDiscord} label={<span className="text-sm">Also post to Discord <span className="text-faint">(#📢-announcements)</span></span>} />
            {discord && (
              <>
                <Switch checked={everyone} onChange={setEveryone} label={<span className="text-sm">Ping <b>@everyone</b></span>} />
                <Switch checked={banner} onChange={setBanner} label={<span className="text-sm">Show the ExoWare banner</span>} />
                <Switch checked={withPoll} onChange={setWithPoll} label={<span className="text-sm">Attach a poll</span>} />
                {withPoll && (
                  <div className="w-full space-y-2 border-t border-line pt-3">
                    <Input placeholder="Poll question" value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={300} />
                    {answers.map((a, i) => (
                      <div key={i} className="flex gap-2">
                        <Input placeholder={`Answer ${i + 1}`} value={a} maxLength={55} onChange={(e) => setAnswers((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))} />
                        {answers.length > 2 && <IconButton icon="x" label="Remove answer" onClick={() => setAnswers((prev) => prev.filter((_, j) => j !== i))} />}
                      </div>
                    ))}
                    <div className="flex items-center justify-between gap-2">
                      {answers.length < 10 ? <Button size="sm" variant="ghost" icon="plus" onClick={() => setAnswers((p) => [...p, ""])}>Add answer</Button> : <span />}
                      <Select value={hours} onChange={(e) => setHours(e.target.value)} className="w-36">
                        <option value="24">Runs 1 day</option><option value="72">Runs 3 days</option><option value="168">Runs 7 days</option><option value="336">Runs 14 days</option>
                      </Select>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <ErrorText>{publish.error}</ErrorText>
          <Button variant="primary" type="submit" icon="send" loading={publish.pending} disabled={!title.trim() || !message.trim() || (discord && withPoll && (!question.trim() || answers.filter((a) => a.trim()).length < 2))} className="w-full">Publish</Button>
        </form>
      </Card>

      <Card title="Preview" description="Updates as you type. This is what members will see.">
        <div className="space-y-5">
          {discord && (
            <div>
              <div className="kicker mb-2">// discord</div>
              <DiscordPreview title={title} message={message} level={level} everyone={everyone} imageUrl={banner ? BANNER : undefined}
                poll={withPoll ? { question, answers, hours: Number(hours) || 72 } : null} />
            </div>
          )}
          <div>
            <div className="kicker mb-2">// website</div>
            <SitePreview title={title} message={message} level={level} />
          </div>
        </div>
      </Card>
    </div>

      <Card title="History" padded={false}>
        {!list.data ? (
          list.loading ? <Loading /> : <LoadError error={list.error} retry={() => void list.reload()} />
        ) : list.data.length === 0 ? (
          <EmptyState icon="megaphone" title="No announcements yet" />
        ) : (
          <ul className="divide-y divide-line">
            {list.data.map((a) => (
              <li key={a.id} className="flex items-start gap-4 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={levelTone[a.level]} dot>{a.level}</Badge>
                    <span className="text-sm font-medium">{a.title}</span>
                  </div>
                  <Markdown text={a.message} className="mt-1.5 text-sm text-muted" />
                  <div className="mt-2 text-xs text-faint" title={fmtDateTime(a.created_at)}>
                    {a.created_by ?? "unknown"} · {fmtRelative(a.created_at)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch checked={a.active} onChange={(v) => void toggle(a, v)} label={<span className="text-xs text-muted">{a.active ? "Active" : "Hidden"}</span>} />
                  <IconButton icon="trash" label="Delete" onClick={() => void remove(a)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Guides() {
  const list = useApi<GuideSummary[]>("/admin/guides");
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<number | "new" | null>(null);

  const toggle = async (g: GuideSummary, active: boolean) => {
    try {
      await api.patch(`/admin/guides/${g.id}`, { active });
      list.setData((prev) => (prev ?? []).map((x) => (x.id === g.id ? { ...x, active } : x)));
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  const remove = async (g: GuideSummary) => {
    if (!await confirm({ title: "Delete guide?", body: `"${g.title}" will be removed permanently.`, confirmLabel: "Delete", danger: true })) return;
    try {
      await api.del(`/admin/guides/${g.id}`);
      list.setData((prev) => (prev ?? []).filter((x) => x.id !== g.id));
      toast("Guide deleted", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Delete failed", "error");
    }
  };

  return (
    <>
      <Card
        title="Guides"
        description="Plain-text help articles. Blank lines start a new paragraph."
        actions={<Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>New guide</Button>}
        padded={false}
      >
        {!list.data ? (
          list.loading ? <Loading /> : <LoadError error={list.error} retry={() => void list.reload()} />
        ) : list.data.length === 0 ? (
          <EmptyState icon="book" title="No guides yet" action={<Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>New guide</Button>} />
        ) : (
          <Table>
            <THead><tr><Th>Title</Th><Th>Slug</Th><Th>Category</Th><Th>Order</Th><Th>Active</Th><Th>Updated</Th><Th /></tr></THead>
            <TBody>
              {list.data.map((g) => (
                <Tr key={g.id}>
                  <Td className="font-medium">{g.title}</Td>
                  <Td><Mono>{g.slug}</Mono></Td>
                  <Td className="text-muted">{g.category || "—"}</Td>
                  <Td className="tabular-nums text-muted">{g.sort_order}</Td>
                  <Td><Switch checked={g.active} onChange={(v) => void toggle(g, v)} /></Td>
                  <Td className="whitespace-nowrap text-muted">{fmtRelative(g.updated_at)}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <IconButton icon="edit" label="Edit" onClick={() => setEditing(g.id)} />
                    <IconButton icon="trash" label="Delete" onClick={() => void remove(g)} />
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} wide title={editing === "new" ? "New guide" : "Edit guide"}>
        {editing !== null && (
          <GuideLoader id={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void list.reload(); }} />
        )}
      </Dialog>
    </>
  );
}

function GuideLoader({ id, onClose, onSaved }: { id: number | "new"; onClose: () => void; onSaved: () => void }) {
  const guide = useApi<Guide>(id === "new" ? null : `/admin/guides/${id}`);
  if (id === "new") return <GuideForm guide={null} onClose={onClose} onSaved={onSaved} />;
  if (!guide.data) return guide.loading ? <Loading /> : <LoadError error={guide.error} retry={() => void guide.reload()} />;
  return <GuideForm guide={guide.data} onClose={onClose} onSaved={onSaved} />;
}

const SLUG_RE = /^[a-z0-9-]{2,100}$/;

function GuideForm({ guide, onClose, onSaved }: { guide: Guide | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [slug, setSlug] = useState(guide?.slug ?? "");
  const [title, setTitle] = useState(guide?.title ?? "");
  const [category, setCategory] = useState(guide?.category ?? "");
  const [sortOrder, setSortOrder] = useState(String(guide?.sort_order ?? 0));
  const [content, setContent] = useState(guide?.content ?? "");
  const [active, setActive] = useState(guide?.active ?? true);

  const slugOk = SLUG_RE.test(slug);
  const save = useAction(async () => {
    const body = { slug, title: title.trim(), content, category: category.trim(), sort_order: Number(sortOrder) || 0 };
    if (guide) await api.patch(`/admin/guides/${guide.id}`, { ...body, active });
    else await api.post("/admin/guides", body);
    toast(guide ? "Guide saved" : "Guide created", "success");
    onSaved();
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save.run(); }} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} required /></Field>
        <Field label="Slug" hint="a-z, 0-9 and hyphens" error={slug && !slugOk ? "2-100 characters: lowercase letters, digits, hyphens" : null}>
          <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="font-mono" required />
        </Field>
        <Field label="Category"><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="General" /></Field>
        <Field label="Sort order"><Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} /></Field>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Content" hint={`${content.length}/50000`}>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)} maxLength={50000} className="h-80 resize-none font-mono text-xs" />
        </Field>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted">Preview</span>
          <div className="h-80 overflow-y-auto rounded-lg border border-line bg-bg p-4">
            {content.trim() ? <Prose text={content} /> : <p className="text-sm text-faint">Nothing to preview yet.</p>}
          </div>
        </div>
      </div>
      {guide && <Switch checked={active} onChange={setActive} label="Active (visible to users)" />}
      <ErrorText>{save.error}</ErrorText>
      <FormActions onCancel={onClose} submitLabel={guide ? "Save changes" : "Create guide"} loading={save.pending} disabled={!title.trim() || !slugOk} />
    </form>
  );
}
